import type { CommandKind } from './commands';

/**
 * Shifts and cash (Phase 7A, ADR-024). A shift is the custody period of one cash drawer, owned by
 * the cashier who opened it. Counts are blind: the server compares a count with the expected cash
 * and answers "count again" (once) without the amount or its direction. The POS never receives the
 * expected cash before a count is final. Everything money-related is append-only on the server;
 * these helpers mirror its rules so the UI can explain them, and unit tests pin the mirror.
 */
export type ShiftStatus = 'open' | 'closing' | 'closed';
export type CountSessionKind = 'open' | 'close' | 'owner_close' | 'handover' | 'takeover';
export type ShiftCloseKind = 'cashier' | 'owner' | 'handover' | 'takeover';
export type ShiftOpenKind = 'open' | 'handover' | 'takeover';

export const PAY_IN_CATEGORIES = ['change_fund', 'owner_cash', 'other'] as const;
export type PayInCategory = (typeof PAY_IN_CATEGORIES)[number];
export const PAY_IN_LABELS: Record<PayInCategory, string> = {
  change_fund: 'Change fund',
  owner_cash: 'Owner cash',
  other: 'Other',
};

/** Owner draw is the owner's alone (cash.receive). */
export const PAY_OUT_CATEGORIES = [
  'ingredients',
  'supplies',
  'delivery',
  'repairs',
  'other',
  'owner_draw',
] as const;
export type PayOutCategory = (typeof PAY_OUT_CATEGORIES)[number];
export const PAY_OUT_LABELS: Record<PayOutCategory, string> = {
  ingredients: 'Ingredients',
  supplies: 'Supplies',
  delivery: 'Delivery / COD',
  repairs: 'Repairs',
  other: 'Other',
  owner_draw: 'Owner draw',
};

/** public.shift_state: what the POS shows. Never expected cash, counts or amounts. */
export interface ShiftStateView {
  ok: true;
  terminal_kind: string;
  employee_id: string | null;
  drawer: { id: string; name: string } | null;
  safe_enabled?: boolean;
  standard_float_minor?: number;
  payout_review_minor?: number;
  shift: ShiftView | null;
  opening: OpeningView | null;
  last_closed?: { id: string; closed_at: string; close_kind: ShiftCloseKind } | null;
  permissions?: { operate: boolean; move: boolean; cover: boolean; receive: boolean };
  server_time: string;
}

export interface ShiftView {
  id: string;
  seq: number;
  status: Exclude<ShiftStatus, 'closed'>;
  business_date: string;
  opened_at: string;
  open_kind: ShiftOpenKind;
  cashier: { id: string; name: string };
  mine: boolean;
  /** Its business date has passed: no money until it is counted. */
  stale: boolean;
  stale_at: string;
  long_open: boolean;
  /** From when another cashier may take it over without the owner. */
  takeover_at: string;
  /** This employee's next closing count on this shift is final (they already counted once). */
  next_count_final: boolean;
  closing: {
    session_id: string;
    kind: Exclude<CountSessionKind, 'open'>;
    started_at: string;
    started_by: { id: string; name: string };
    mine: boolean;
    /** The final count is in: finish or cancel. */
    counted: boolean;
    /** That final count, for whoever finishes it (no longer blind once it is final). A takeover's
     * counter gets the count only: the difference is the previous cashier's (and the owner's). */
    final_count: {
      counted_minor: number;
      over_short_minor?: number;
      within_tolerance?: boolean;
      needs_reason: boolean;
    } | null;
    too_long: boolean;
  } | null;
  /** The shift's latest cash entries, for its cashier and the owner (null for anyone else). */
  movements: ShiftMovementView[] | null;
  /** What anyone other than the cashier did with the shift's money (the owner at the till), for
   * its cashier (null for anyone else). */
  others: ShiftOtherView[] | null;
}

export interface ShiftMovementView {
  id: string;
  kind: Exclude<CashMovementKind, 'removal' | 'void'>;
  amount_minor: number;
  category: string | null;
  payee: string | null;
  reason: string | null;
  envelope_no: number | null;
  recorded_at: string;
  by: string;
  /** Recorded by the signed-in employee. */
  mine: boolean;
  voided: boolean;
}

export interface ShiftOtherView {
  id: string;
  action: string;
  by: string | null;
  method?: string | null;
  category?: string | null;
  /** It changes the cash in the drawer. */
  cash: boolean;
  amount_minor: number;
  at: string;
}

export interface OpeningView {
  /** The first shift on this drawer: nothing is expected yet. */
  first_shift: boolean;
  next_count_final: boolean;
  /** Someone else's unfinished opening count on this drawer. */
  counting: string | null;
  previous: { id: string; cashier: string; closed_at: string; close_kind: ShiftCloseKind } | null;
}

export type ShiftStateResult = ShiftStateView | { ok: false; code: 'terminal_revoked' };

/** The answer to a blind count (shift.open and shift.count). A takeover's final count carries no
 * difference for a counter who is not the owner: it is the previous cashier's. */
export type CountResult =
  | { result: 'count_again'; attempt: number }
  | {
      result: 'counted';
      final: true;
      attempt: number;
      over_short_minor?: number;
      within_tolerance?: boolean;
      needs_reason: boolean;
    };

export type OpenResult =
  | { result: 'count_again'; attempt: number; drawer_id: string }
  | { result: 'opened'; shift_id: string; seq: number; business_date: string; drawer_id: string };

/* ── the drawer's expected cash (mirrors app.shift_expected_cash) ── */

export interface DrawerFlows {
  openingCountMinor: number;
  /** Cash payments of the shift, already net of change. */
  cashCapturedMinor: number;
  /** Cash given back on the shift, by the refund method (a GCash sale refunded in cash counts). */
  cashReturnedMinor: number;
  payInsMinor: number;
  payOutsMinor: number;
  safeDropsMinor: number;
}

export type CashMovementKind = 'pay_in' | 'pay_out' | 'safe_drop' | 'no_sale' | 'removal' | 'void';

/** What one cash movement does to the drawer. A void cancels the entry it names; a removal at the
 * close is custody (after the count), never drawer flow. */
export function movementEffectMinor(
  kind: CashMovementKind,
  amountMinor: number,
  voidedKind?: CashMovementKind,
): number {
  switch (kind) {
    case 'pay_in':
      return amountMinor;
    case 'pay_out':
    case 'safe_drop':
      return -amountMinor;
    case 'void':
      return voidedKind ? -movementEffectMinor(voidedKind, amountMinor) : 0;
    default:
      return 0;
  }
}

export function expectedCashMinor(f: DrawerFlows): number {
  return (
    f.openingCountMinor +
    f.cashCapturedMinor -
    f.cashReturnedMinor +
    f.payInsMinor -
    f.payOutsMinor -
    f.safeDropsMinor
  );
}

/** Positive: more cash than expected (over); negative: short. */
export function overShortMinor(countedMinor: number, expectedMinor: number): number {
  return countedMinor - expectedMinor;
}

/**
 * The server's recount rule: a count within the tolerance of what is expected is final; otherwise
 * the counter's first count on it is answered "count again" and their second count is final
 * (a cancelled count does not reset it). Nothing expected (the first shift on a drawer): final.
 */
export function countOutcome(input: {
  countedMinor: number;
  expectedMinor: number | null;
  toleranceMinor: number;
  priorAttempts: number;
}): { final: boolean; withinTolerance: boolean; differenceMinor: number | null } {
  if (input.expectedMinor === null)
    return { final: true, withinTolerance: true, differenceMinor: null };
  const differenceMinor = input.countedMinor - input.expectedMinor;
  const withinTolerance = Math.abs(differenceMinor) <= input.toleranceMinor;
  return { final: withinTolerance || input.priorAttempts >= 1, withinTolerance, differenceMinor };
}

/** "₱60.00 over", "₱10.00 short" or "Balanced". */
export function describeOverShort(minor: number, format: (minor: number) => string): string {
  if (minor === 0) return 'Balanced';
  return minor > 0 ? `${format(minor)} over` : `${format(-minor)} short`;
}

/** What may leave the drawer at a close: the count minus what is kept (never negative). */
export function removedMinor(countedMinor: number, retainedMinor: number): number {
  return Math.max(0, countedMinor - Math.min(Math.max(0, retainedMinor), countedMinor));
}

/**
 * The amount suggested to keep at a close: the standard float when set and the safe takes the
 * rest; otherwise everything counted (without a safe, cash leaves only into the owner's hands).
 */
export function suggestedKeepMinor(
  countedMinor: number,
  standardFloatMinor: number,
  safeEnabled: boolean,
): number {
  if (!safeEnabled || standardFloatMinor <= 0) return countedMinor;
  return Math.min(countedMinor, standardFloatMinor);
}

/** Shift and cash commands, and every command that gives money back, need the server now: the
 * POS sends them only while online and never queues them (ADR-024). Captures may still queue. */
export const ONLINE_ONLY_KINDS = [
  'shift.open',
  'shift.close_start',
  'shift.handover_start',
  'shift.count',
  'shift.close_finish',
  'shift.close_cancel',
  'shift.takeover_start',
  'shift.takeover_finish',
  'cash.pay_in',
  'cash.pay_out',
  'cash.safe_drop',
  'cash.no_sale',
  'cash.movement_void',
  'payment.discard_queued',
  'shift.review_ack',
  'shift.mark_reviewed',
  'shift.mark_day_reviewed',
  'shift.confirm_pin_day',
  'cash.reconcile',
  'cash.reconcile_all',
  'shift.correct',
  'shift.correct_undo',
  'shift.payment_missed',
  'shift.refund_missed',
  'cash.give_back_reference',
  'cash.give_back_outcome',
  'payment.void',
  'order.refund',
  'order.void',
  /* ADR-031: a write-off reads the day's allowance on the server; never queued */
  'order.write_off',
  'order.write_off_reinstate',
  /* ADR-032: a return reads the same allowance and may give money back; never queued */
  'order.return_line',
  /* ADR-033: the owner's phone; never queued */
  'approval.grant_remote',
  'approval.decline_remote',
  'order.refund_outside_shift',
] as const satisfies readonly CommandKind[];

export function isOnlineOnly(kind: string): boolean {
  return (ONLINE_ONLY_KINDS as readonly string[]).includes(kind);
}

/** What a tablet reports in its heartbeat so a count elsewhere on its drawer can wait for it. */
export interface HeartbeatState {
  online: boolean;
  session: boolean;
  /** Commands this tablet is still sending (a count elsewhere on its drawer waits for them). */
  pending: number;
  /** Commands not being sent: waiting for an employee who is not signed in here, or queued
   * payments the server refused and kept here until sent again or discarded. */
  parked: number;
}

/** Hints the server attaches to shift refusals (`hint.shift`). */
export type ShiftRefusal =
  | 'no_drawer'
  | 'none'
  | 'closing'
  | 'stale'
  | 'other'
  | 'changed'
  | 'late_refused'
  | 'late_expired'
  | 'counted'
  | 'not_counted'
  | 'takeover';
